import React, { useState } from 'react';
import type { FormikTouched } from 'formik';
import { Formik, useFormikContext } from 'formik';
import { isEmpty, isNil, trim } from 'lodash';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import { Text } from '~/components/core';
import MenuItem from '~/components/core/Atomic/MenuItem';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import SingleSelectField from '~/components/core/Molecules/Fields/SingleSelectField';
import TextField from '~/components/core/Molecules/Fields/TextField';
import TextFieldFormik from '~/components/TextFieldFormik';
import type { FieldGroupSource, HierarchySubField } from '~/components/types/incident-configuration-types';

import { fieldGroupSourceSchema } from '../../fnolConfigurationValidationSchema';
import copies from '../copies.json';

import StaticTreeField from './StaticTreeField';

const TEXTS = copies.field_groups;

// The two shapes `FieldGroupSource` allows. Exactly one is ever set, which is why switching writes
// a whole new `source` rather than adding a key beside the one already there.
const SOURCE_KIND_LABELS = {
  endpoint: TEXTS.source_kind_endpoint,
  static_json: TEXTS.source_kind_static,
} as const;

type SourceKind = keyof typeof SOURCE_KIND_LABELS;

// A tree with no nodes is legitimate - a live source has nothing for some policies - so it is also
// what a new static tree starts from, rather than a placeholder the admin has to delete.
const EMPTY_SOURCE_BY_KIND: Record<SourceKind, FieldGroupSource> = {
  endpoint: { source_key: '', url: '', auth_token: '' },
  static_json: { static_json: { nodes: [] } },
};

/**
 * An example payload for *this* group's levels: one node per level, nested in the levels' own order,
 * each id qualified by its parent's.
 *
 * Derived from the group's `sub_fields` rather than written out, so it follows a level being added,
 * removed or renamed and cannot drift from the contract
 * `server/external_hierarchy_api/schemas/hierarchy_api_contract_schemas.py` enforces at save time -
 * depth is the level count, and ids must be unique across the whole payload and sit under one parent.
 */
export const buildExampleHierarchyTree = (subFields: Partial<HierarchySubField>[]): Record<string, unknown> => {
  const buildLevel = (depth: number, parentId: string): Record<string, unknown>[] => {
    const subField = subFields[depth];
    if (isNil(subField)) {
      return [];
    }
    const levelKey = subField.id || `level_${depth + 1}`;
    const id = isEmpty(parentId) ? `${levelKey}_1` : `${parentId}_${levelKey}_1`;
    const children = buildLevel(depth + 1, id);
    const node = { id, desc: `${subField.desc || levelKey} 1` };
    return [isEmpty(children) ? node : { ...node, children }];
  };

  return { nodes: buildLevel(0, '') };
};

/** What both sources answer with, shown beside the source picker so either kind has the shape. */
const HierarchyExample: React.FC<{ subFields: Partial<HierarchySubField>[] }> = ({ subFields }) => (
  <div className="flex flex-col gap-8">
    <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
      {TEXTS.source_example_note}
    </Text>
    <pre className="font-mono m-0 overflow-auto rounded-xl border border-slate-300 bg-slate-100 p-16 text-xs">
      {JSON.stringify(buildExampleHierarchyTree(subFields), null, 2)}
    </pre>
  </div>
);

/** The dialog's own form: a draft of the group's source, handed back to the screen's form on Update. */
interface SourceDraft {
  source: FieldGroupSource;
}

// The screen validates the same `source` on save, so Update refuses exactly what that save would.
const sourceDraftSchema = Yup.object({ source: fieldGroupSourceSchema });

interface FieldGroupSourceDialogProps {
  /** The group's display name, which titles the dialog. */
  fieldGroupDesc: string;
  /** The source as the screen's form holds it; the dialog edits a draft of it. */
  source: FieldGroupSource;
  /**
   * The source fields whose errors the card already shows, so the dialog opens showing them on
   * the same inputs instead of waiting for them to be touched again.
   */
  sourceTouched: FormikTouched<FieldGroupSource>;
  /** The group's levels, which the example payload is built from. */
  subFields: Partial<HierarchySubField>[];
  isNewFieldGroup: boolean;
  /** Every field read-only, and the dialog can only be closed. */
  disabled: boolean;
  onUpdate: (source: FieldGroupSource) => Promise<void>;
  onClose: () => void;
}

type FieldGroupSourceDialogBodyProps = Omit<FieldGroupSourceDialogProps, 'source' | 'sourceTouched' | 'onUpdate'>;

const FieldGroupSourceDialogBody: React.FC<FieldGroupSourceDialogBodyProps> = ({
  fieldGroupDesc,
  subFields,
  isNewFieldGroup,
  disabled,
  onClose,
}) => {
  const { values, setFieldValue, handleSubmit, isSubmitting } = useFormikContext<SourceDraft>();
  const [documentError, setDocumentError] = useState<string>();
  const isStaticBacked = !isNil(values.source.static_json);

  return (
    <CardDialog
      isDialog
      open
      title={
        isEmpty(trim(fieldGroupDesc)) ? TEXTS.source_title : TEXTS.source_dialog_title.replace('{name}', fieldGroupDesc)
      }
      fullWidth
      maxWidth="md"
      onClose={onClose}
      preventClose={isSubmitting}
      // jsoneditor appends its Sort and Transform modals to document.body, outside this dialog, and
      // the dialog's focus trap would pull focus straight back out of their inputs.
      disableEnforceFocus
      footerActions={
        disabled ? (
          <DialogFooterActions showPrimary={false} secondaryLabel={TEXTS.close} onClickSecondary={onClose} />
        ) : (
          <DialogFooterActions
            primaryLabel={TEXTS.update}
            disabled={isSubmitting}
            // A tree the editor could not store is not in the draft for the schema to see, so it
            // holds Update back here; the rest of the draft is refused by the schema on submit.
            primaryDisabled={!isNil(documentError)}
            onClickPrimary={() => handleSubmit()}
            onClickSecondary={onClose}
          />
        )
      }
    >
      <div className="grid grid-cols-12 items-center gap-24">
        <div className="col-span-6">
          {/* Locked once saved, exactly as Type is: switching kind drops the other key from
              `source`, and the shared config-diff guard refuses any removal from a saved
              configuration. A saved group changes source by being retired and replaced. */}
          <SingleSelectField<SourceKind>
            label={TEXTS.source_kind_label}
            value={isStaticBacked ? 'static_json' : 'endpoint'}
            fullWidth
            disabled={disabled || !isNewFieldGroup}
            onChange={(selectedKind) => setFieldValue('source', EMPTY_SOURCE_BY_KIND[selectedKind])}
          >
            {Object.entries(SOURCE_KIND_LABELS).map(([sourceKind, label]) => (
              <MenuItem key={sourceKind} value={sourceKind}>
                {label}
              </MenuItem>
            ))}
          </SingleSelectField>
        </div>
        {/* Above both source editors rather than inside either: the endpoint has to answer with
            this shape and the static tree has to be pasted in it, so it is the one thing they share. */}
        <div className="col-span-12">
          <HierarchyExample subFields={subFields} />
        </div>
        {isStaticBacked ? (
          <div className="col-span-12">
            <StaticTreeField
              staticJsonPath="source.static_json"
              disabled={disabled}
              onDocumentErrorChange={setDocumentError}
            />
          </div>
        ) : (
          <>
            <div className="col-span-6">
              {/* Locked once saved, as the group's own Key is: the key is what a promotion of this
                  configuration carries to another environment, so repointing a saved group at a
                  different source would silently repoint every environment it has been promoted
                  to. A saved group changes source by being retired and replaced. */}
              {isNewFieldGroup ? (
                <TextFieldFormik id="source.source_key" label={TEXTS.source_key_label} fullWidth disabled={disabled} />
              ) : (
                <TextField
                  id="source.source_key"
                  label={TEXTS.source_key_label}
                  value={values.source.source_key ?? ''}
                  fullWidth
                  disabled
                />
              )}
            </div>
            <div className="col-span-6" />
            <div className="col-span-12">
              <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                {TEXTS.source_key_note}
              </Text>
            </div>
            <div className="col-span-6">
              {/* The URL and the token below are stored on the source row the key names, not on
                  this configuration - so they stay behind when it is promoted, and editing them
                  here edits that row for every group sharing the key. */}
              <TextFieldFormik id="source.url" label={TEXTS.endpoint_url_label} fullWidth disabled={disabled} />
            </div>
            <div className="col-span-6">
              {/* The GET hands back '*****' when the source row holds a token and null when it
                  does not, and posting '*****' back means "keep it" - so an untouched field
                  submits the mask as-is and the plaintext never has to make the round trip. */}
              <TextFieldFormik
                id="source.auth_token"
                label={TEXTS.auth_token_label}
                // @ts-expect-error type is not defined in TextFieldFormik
                type="password"
                fullWidth
                disabled={disabled}
              />
            </div>
          </>
        )}
      </div>
    </CardDialog>
  );
};

/**
 * The group's hierarchy source, edited apart from the card: the dialog holds its own draft, so
 * Cancel drops every edit, and Update hands the whole draft back for the card to store.
 */
const FieldGroupSourceDialog: React.FC<FieldGroupSourceDialogProps> = ({
  source,
  sourceTouched,
  onUpdate,
  ...bodyProps
}) => (
  <Formik<SourceDraft>
    initialValues={{ source }}
    initialTouched={{ source: sourceTouched }}
    validationSchema={sourceDraftSchema}
    validateOnMount
    onSubmit={({ source: draft }) => onUpdate(draft)}
  >
    <FieldGroupSourceDialogBody {...bodyProps} />
  </Formik>
);

export default FieldGroupSourceDialog;
